import { teacherProfileAtom } from '@openeducation/oe-it-jotai-atoms';
import { NotificationService } from '@openeducation/oe-it-services';
import {
  GenericNotification,
  LiveClassNotification,
  LiveClassTeacherReminderNotification,
  NotificationDesktop,
  NotificationType,
} from '@openeducation/oe-it-shared-types';
import constate from 'constate';
import { useAtomValue } from 'jotai';
import { useEffect, useState, useCallback } from 'react';
import { useMutation } from 'react-query';

import { useEnvironmentContext } from '../environment/environment.provider';
import { useGenericNotifications } from '../hooks';

export interface NotificationContextProps {
  showNotification: (notification: NotificationDesktop) => void;
}

enum QueryKeys {
  LiveClassTeacherReminderNotification = 'live-class-teacher-reminder-notification',
  LiveClassNotification = 'live-class-notification',
  GenericNotification = 'generic-notification',
}

const NotificationContext = ({
  showNotification,
}: NotificationContextProps) => {
  const { environment } = useEnvironmentContext();

  const teacherProfile = useAtomValue(teacherProfileAtom);
  const [confirmedNotification, setConfirmedNotification] = useState<string[]>(
    []
  );
  const [notificationClicked, setNotificationClicked] = useState('');

  const {
    data: lcTeacherReminderNotifications,
    error: errorLCTeacherReminderNotifications,
    isLoading: isLoadingLCTeacherReminderNotifications,
    refetch: refetchLCTeacherReminderNotifications,
  } = useGenericNotifications<LiveClassTeacherReminderNotification>({
    queryKey: QueryKeys.LiveClassTeacherReminderNotification,
    notificationType: NotificationType.LiveClassTeacherReminderNotification,
  });

  const {
    data: lcNotifications,
    error: errorLCNotifications,
    isLoading: isLoadingLCNotifications,
    refetch: refetchLCNotifications,
  } = useGenericNotifications<LiveClassNotification>({
    queryKey: QueryKeys.LiveClassNotification,
    notificationType: NotificationType.LiveClassNotification,
  });

  const {
    data: genericNotifications,
    error: errorGenericNotifications,
    isLoading: isLoadingGenericNotifications,
    refetch: refetchGenericNotifications,
  } = useGenericNotifications<GenericNotification>({
    queryKey: QueryKeys.GenericNotification,
  });

  const { mutateAsync: confirmNotification } = useMutation((id: string) => {
    return NotificationService.confirm(environment, id, teacherProfile?.id);
  });

  const { mutateAsync: acknowledgeNotification } = useMutation((id: string) => {
    return NotificationService.acknowledge(environment, id, teacherProfile?.id);
  });

  const shouldConfirm = useCallback(
    (
      notification:
        | LiveClassTeacherReminderNotification
        | GenericNotification
        | LiveClassNotification
    ) => {
      if (!confirmedNotification.includes(notification.id)) {
        setConfirmedNotification((notifications: string[]) => [
          ...notifications,
          notification.id,
        ]);
        return true;
      }
      return false;
    },
    [confirmedNotification, setConfirmedNotification]
  );

  useEffect(() => {
    lcTeacherReminderNotifications?.map((notification) => {
      if (shouldConfirm(notification)) {
        switch (notification.notification) {
          case 'TeachingTimeStartNotification': {
            showNotification({
              title: 'Teaching Time',
              body: 'Time to start teaching!',
              icon: 'join',
            });

            break;
          }
          case 'EmptyClassNotification': {
            showNotification({
              title: 'Empty Class',
              body: 'Classroom appears empty. Please click to close classroom.',
              icon: 'close',
            });
            break;
          }
          case 'FeedbackTimeStartNotification': {
            showNotification({
              title: 'Teaching Time',
              body: 'Dismiss class',
            });
            break;
          }
        }
        confirmNotification(notification.id);
      }
    });
  }, [
    confirmNotification,
    lcTeacherReminderNotifications,
    shouldConfirm,
    showNotification,
  ]);

  useEffect(() => {
    genericNotifications?.map((notification) => {
      if (shouldConfirm(notification)) {
        confirmNotification(notification.id);
      }
    });
  }, [confirmNotification, genericNotifications, shouldConfirm]);

  useEffect(() => {
    lcNotifications?.map((notification) => {
      if (shouldConfirm(notification)) {
        confirmNotification(notification.id);

        showNotification({
          title: 'Classroom Ready',
          body: notification.notification,
          icon: 'join',
          onClick: () => {
            setNotificationClicked(notification.url);
          },
        });
      }
    });
  }, [confirmNotification, lcNotifications, shouldConfirm, showNotification]);

  const classEmpty = lcTeacherReminderNotifications?.find((notification) => {
    return notification.notification === 'EmptyClassNotification';
  });

  const reset = () => {
    refetchLCNotifications();
    refetchLCTeacherReminderNotifications();
    refetchGenericNotifications();
  };
  return {
    environment,
    showNotification,
    classEmpty,
    acknowledgeNotification,
    confirmNotification,
    notificationClicked,
    cleanNotificationClicked: () => setNotificationClicked(''),
    reset,
    // Live Class Teacher Reminder Notifications
    lcTeacherReminderNotifications,
    errorLCTeacherReminderNotifications,
    isLoadingLCTeacherReminderNotifications,
    // Live Class Notifications
    lcNotifications,
    errorLCNotifications,
    isLoadingLCNotifications,
    //Generic Notifications
    genericNotifications,
    errorGenericNotifications,
    isLoadingGenericNotifications,
  };
};

const [NotificationProviderCtx, useNotificationContext] =
  constate(NotificationContext);
export interface NotificationProviderProps extends NotificationContextProps {
  children?: React.ReactNode;
}

const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  ...rest
}) => <NotificationProviderCtx {...rest}>{children}</NotificationProviderCtx>;

export { NotificationProvider, useNotificationContext };
